import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import { Breadcrumb, Segmented, Spin } from "antd";
import { observer } from "mobx-react-lite";

import { getSubscriptionPlans, mapSubscriptionPlan } from "../api/subscription";
import { SubscriptionPlans } from "../components/subscription/SubscriptionPlans";
import { userStore } from "../stores/userStore";
import { sendCounterEvent } from "../utils/counterService";
import { usePageTitle } from "../utils/usePageTitle";

import "./SubscriptionUpdatePage.css";

import type {
  SubscriptionPlan,
  SubscriptionPlanRaw,
} from "../api/subscription";

export const SubscriptionUpdatePage = observer(
  function SubscriptionUpdatePage() {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.dir() === "rtl";
    const navigate = useNavigate();
    const sub = userStore.subscription;
    const [rawPlans, setRawPlans] = useState<SubscriptionPlanRaw[]>([]);
    const [loading, setLoading] = useState(true);
    const [isYearly, setIsYearly] = useState(false);

    usePageTitle(t("subscription.titleChange"));

    useEffect(() => {
      if (sub?.interval === "yearly") setIsYearly(true);
    }, [sub?.interval]);

    useEffect(() => {
      let active = true;
      getSubscriptionPlans()
        .then((data) => {
          if (!active) return;
          setRawPlans(data);
          setLoading(false);
        })
        .catch((err) => {
          if (active && err.name !== "AbortError") setLoading(false);
        });
      return () => {
        active = false;
      };
    }, []);

    const currency = userStore.currency;
    const plans = rawPlans.map((p) => mapSubscriptionPlan(p, currency));
    const yearlyDiscount =
      rawPlans.find((p) => p.id === "pomelo")?.discount_annual ?? null;

    const handleSelect = (plan: SubscriptionPlan, period: string) => {
      sendCounterEvent(`choose-${plan.id}`, { ...plan });
      navigate(
        plan.id === "lemon"
          ? "/subscription/update/cancel"
          : `/subscription/update/manage?plan=${plan.id}&period=${period}`,
      );
    };

    return (
      <div className="page subscription-update-page">
        <Breadcrumb
          className="subscription-update-page__breadcrumb"
          separator={
            <Icon path={isRtl ? mdiChevronLeft : mdiChevronRight} size={0.7} />
          }
          items={[
            {
              title: <Link to="/subscription">{t("subscription.title")}</Link>,
            },
            { title: t("subscription.titleChange") },
          ]}
        />

        <div className="subscription-update-page__header">
          <h1>{t("subscription.titleChange")}</h1>
          {!loading && (
            <Segmented
              size="large"
              value={isYearly ? "yearly" : "monthly"}
              onChange={(val) => setIsYearly(val === "yearly")}
              options={[
                { value: "monthly", label: t("subscription.payMonthly") },
                {
                  value: "yearly",
                  label: (
                    <span>
                      {t("subscription.payAnnually")}
                      {yearlyDiscount && (
                        <span className="subscription-update-page__discount-chip">
                          {t("subscription.savePercent", {
                            percent: yearlyDiscount,
                          })}
                        </span>
                      )}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </div>

        {loading ? (
          <div className="subscription-update-page__loading">
            <Spin size="large" />
          </div>
        ) : (
          <SubscriptionPlans
            plans={plans}
            isYearly={isYearly}
            onSelect={handleSelect}
          />
        )}
      </div>
    );
  },
);
